/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

// src/components/applications/FieldUiRulesDialog.js

import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../../theme/ThemeContext";
import Button from "../Button";
import ThemedToggle from "../common/ThemedToggle";

/**
 * FieldUiRulesDialog
 * ------------------
 * A modal dialog to configure `ui_rules` JSON for a single field.
 *
 * Props:
 *  - open: boolean
 *  - field: {
 *      name: string;
 *      display_name?: string;
 *      data_type: string;
 *      ui_rules?: any;
 *    }
 *  - fields: array of all fields in this table
 *      Used for:
 *        - column dropdowns in visible_if / readonly_if
 *        - max_length lookup for default input
 *  - roles: Array<{ value: string; label: string }> | string[]
 *      List of available roles in this table/application.
 *  - required?: boolean
 *      When true:
 *        - Static default (ui_rules.default) is mandatory.
 *        - Field must always be visible; role-based visibility UI is hidden.
 *  - onClose: () => void
 *  - onSave: (uiRules: any) => void
 *
 * ui_rules (relevant parts):
 *  - visible, visible_if.{ role_in, value_equals }
 *  - readonly, readonly_if.{ role_in, value_equals }
 *  - default, default_expression
 *  - options_source: { type: "static", values: [{label,value}] } | { type: "query", ... }
 *  - options_by_role: { per_role: { [role]: [{label,value}] } }
 *  - hint_by_role: { [role]: string }
 *  - order, widget.{ placeholder }
 */
export default function FieldUiRulesDialog({
    open,
    field,
    fields = [],
    roles = [],
    required = false,
    onClose,
    onSave,
}) {
    const { theme } = useTheme();
    // Normalize incoming roles into consistent { value, label } objects
    const normalizedRoles = useMemo(() => {
        console.log("[Roles] Raw roles input:", roles);

        const rolesArray = Array.isArray(roles)
            ? roles
            : roles?.results ?? [];

        const result = rolesArray.map((r, index) => {
            let normalized;

            if (typeof r === "string") {
                normalized = { value: r, label: r };
            }
            else if (r.value && r.label) {
                // ✅ already normalized
                normalized = r;
            }
            else if (r.name) {
                // API object
                normalized = {
                    value: r.name,
                    label: r.label || r.name,
                };
            }
            else {
                console.warn(`[Roles] [${index}] Unknown role shape:`, r);
                normalized = null;
            }

            console.log(`[Roles] [${index}] Normalized:`, normalized);
            return normalized;
        }).filter(Boolean);

        console.log("[Roles] Final normalizedRoles:", result);
        return result;
    }, [roles]);



    // List of raw role values (e.g. ["Admin", "Staff"])
    const roleValues = useMemo(() => {
        console.log("[Roles] Computing roleValues from normalizedRoles:", normalizedRoles);

        const values = normalizedRoles.map((r, index) => {
            console.log(`[Roles] [${index}] Extracting value:`, r.value);
            return r.value;
        });

        console.log("[Roles] Final roleValues:", values);
        return values;
    }, [normalizedRoles]);

    // Helper to get a human-readable label for a role value
    const getRoleLabel = (value) =>
        normalizedRoles.find((r) => r.value === value)?.label || value;

    // --- Local ui_rules state ---

    // Visibility
    const [visible, setVisible] = useState(true);
    const [roleVisibleIn, setRoleVisibleIn] = useState([]); // visible_if.role_in
    const [visibleCondField, setVisibleCondField] = useState(""); // visible_if.value_equals key
    const [visibleCondValue, setVisibleCondValue] = useState(""); // visible_if.value_equals value

    // Read-only
    const [readonly, setReadonly] = useState(false);
    const [roleReadonlyIn, setRoleReadonlyIn] = useState([]); // readonly_if.role_in
    const [readonlyCondField, setReadonlyCondField] = useState(""); // readonly_if.value_equals key
    const [readonlyCondValue, setReadonlyCondValue] = useState(""); // readonly_if.value_equals value

    // Defaults
    const [defaultValue, setDefaultValue] = useState(""); // ui_rules.default
    const [defaultExpression, setDefaultExpression] = useState(""); // ui_rules.default_expression
    const [defaultError, setDefaultError] = useState(""); // validation error for static default
    const [expressionError, setExpressionError] = useState(""); // NEW: validation error for dynamic default

    // Global options_source
    const [optionsSourceType, setOptionsSourceType] = useState("none"); // "none" | "static" | "query"
    const [staticOptions, setStaticOptions] = useState([]); // [{label,value}]
    const [newOptionLabel, setNewOptionLabel] = useState("");
    const [newOptionValue, setNewOptionValue] = useState("");

    // Role-based options (options_by_role.per_role)
    const [roleOptionsByRole, setRoleOptionsByRole] = useState({}); // { [role]: [{label,value}] }
    const [selectedRoleForOptions, setSelectedRoleForOptions] = useState(null);
    const [rbNewOptionLabel, setRbNewOptionLabel] = useState("");
    const [rbNewOptionValue, setRbNewOptionValue] = useState("");

    // Role-based hints (hint_by_role)
    const [roleHints, setRoleHints] = useState({}); // { [role]: string }
    const [selectedRoleForHint, setSelectedRoleForHint] = useState(null);
    const [currentHintText, setCurrentHintText] = useState("");

    // Layout & widget
    const [order, setOrder] = useState("");
    const [placeholder, setPlaceholder] = useState("");

    // Dynamic default info toggle
    const [showDynamicInfo, setShowDynamicInfo] = useState(false); // NEW: collapsible help

    // Display as Badge settings
    const [displayAsBadge, setDisplayAsBadge] = useState(false);
    const [badgeColor, setBadgeColor] = useState("#0d6efd"); // default bootstrap primary color

    // --- helpers to look up full field info (for data_type / max_length) ---

    /** Find a field from the full fields[] list by its name. */
    const findFieldByName = (name) =>
        (fields || []).find((f) => f.name === name) || null;

    /**
     * For the current field (this column), get max length when data_type is string.
     * Falls back to 255 if not found or invalid.
     */
    const getThisFieldStringMaxLength = () => {
        if (!field || !field.name) return 255;
        const full = findFieldByName(field.name);
        if (!full) return 255;
        const raw = full.max_length ?? full.maxLength;
        if (!raw) return 255;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : 255;
    };

    /** For a referenced field (conditions), get its data_type in lowercase. */
    const getFieldDataType = (name) => {
        const f = findFieldByName(name);
        return f && f.data_type ? f.data_type.toLowerCase() : "";
    };

    // --- Dynamic default validation helpers ---

    /**
     * Valid expression patterns supported:
     *
     * 1) Single tokens:
     *    - now()
     *    - current_user.email
     *    - current_user.username
     *    - form.<field>
     *
     * 2) Binary expressions of the same kind:
     *    - form.<field> <op> form.<field>
     *    - now() <op> now()
     *    - current_user.email <op> current_user.email (theoretically allowed)
     *
     *    where <op> is one of: +, -, *, /, %, ==, !=, <, <=, >, >=
     *
     * 3) String concatenation with literals:
     *    - 'PREFIX' + current_user.email + 'SUFFIX'
     *    - 'MR ' + form.name + ' SUF'
     *
     * Rules:
     *  - Only one category at a time (no mixing now() with form.* etc except via concatenation).
     *  - Concatenation uses +, and every non-literal token must be one of:
     *      now(), current_user.email, current_user.username, form.<field>
     */
    const VALID_BINARY_OPERATORS = [
        "+",
        "-",
        "*",
        "/",
        "%",
        "==",
        "!=",
        "<",
        "<=",
        ">",
        ">=",
        "||",
        "&&"
    ];

    const isIdentifierToken = (token) => {
        const trimmed = token.trim();
        if (trimmed === "now()") return true;
        if (trimmed === "current_user.email") return true;
        if (trimmed === "current_user.username") return true;
        if (/^form\.[A-Za-z_][A-Za-z0-9_]*$/.test(trimmed)) return true;
        return false;
    };

    const isLiteralStringToken = (token) => {
        const t = token.trim();
        // Simple single-quoted literal, allow anything except unescaped single quote in the middle
        return /^'[^']*'$/.test(t);
    };

    /**
     * Validate dynamic default expression according to the above rules.
     * Returns { valid: boolean, error: string }.
     */
    const validateDynamicExpression = (exprRaw) => {
        const expr = (exprRaw || "").trim();
        if (!expr) {
            // Empty is allowed (means "no dynamic default")
            return { valid: true, error: "" };
        }

        // 1) Pure concatenation with '+' of allowed tokens and literals
        //    Split on '+' but keep binary ops case below; here we only accept '+'.
        const plusParts = expr.split("+");
        if (plusParts.length > 1) {
            const badToken = plusParts.find(
                (p) => !isLiteralStringToken(p) && !isIdentifierToken(p)
            );
            if (badToken) {
                return {
                    valid: false,
                    error:
                        "Dynamic default with '+' must only combine 'now()', current_user.email, current_user.username, form.<field> and single-quoted text literals.",
                };
            }
            // Concatenation is always category-wise safe because each non-literal is validated individually.
            return { valid: true, error: "" };
        }

        // 2) Check for simple binary expression with one of the allowed operators
        for (const op of VALID_BINARY_OPERATORS) {
            const idx = expr.indexOf(` ${op} `);
            if (idx !== -1) {
                const left = expr.slice(0, idx);
                const right = expr.slice(idx + op.length + 2);
                if (!isIdentifierToken(left) || !isIdentifierToken(right)) {
                    return {
                        valid: false,
                        error:
                            "Both sides of a binary expression must be one of: now(), current_user.email, current_user.username, or form.<field>.",
                    };
                }
                // Both sides are valid identifiers of the same "kind" category.
                // Enforce mutual exclusivity: same category left/right.
                const kind = (t) => {
                    const v = t.trim();
                    if (v === "now()") return "now";
                    if (v === "current_user.email" || v === "current_user.username")
                        return "user";
                    if (v.startsWith("form.")) return "form";
                    return "other";
                };
                const k1 = kind(left);
                const k2 = kind(right);
                if (k1 !== k2) {
                    return {
                        valid: false,
                        error:
                            "Binary expressions must not mix now(), current_user.* and form.*. Use the same source on both sides.",
                    };
                }
                return { valid: true, error: "" };
            }
        }

        // 3) Single token: must be a valid identifier
        if (isIdentifierToken(expr)) {
            return { valid: true, error: "" };
        }

        // If nothing matched, the pattern is invalid
        return {
            valid: false,
            error:
                "Dynamic default must be now(), current_user.email, current_user.username, form.<field>, a binary expression between two such values, or a '+' concatenation with single-quoted text.",
        };
    };

    // --- Initialize local state from incoming field.ui_rules ---

    useEffect(() => {
        if (!open || !field) return;
        const ui = field.ui_rules || {};

        // Visibility
        if (required) {
            // Required fields must always be visible; ignore persisted visible_if
            setVisible(true);
            setRoleVisibleIn([]);
            setVisibleCondField("");
            setVisibleCondValue("");
        } else {
            setVisible(ui.visible !== false);
            const visibleIf = ui.visible_if || {};
            setRoleVisibleIn(visibleIf.role_in || []);
            if (visibleIf.value_equals) {
                const keys = Object.keys(visibleIf.value_equals);
                if (keys.length > 0) {
                    const k = keys[0];
                    const v = visibleIf.value_equals[k];
                    setVisibleCondField(k);
                    setVisibleCondValue(Array.isArray(v) ? v[0] : v);
                } else {
                    setVisibleCondField("");
                    setVisibleCondValue("");
                }
            } else {
                setVisibleCondField("");
                setVisibleCondValue("");
            }
        }

        // Readonly
        setReadonly(ui.readonly === true);
        const readonlyIf = ui.readonly_if || {};
        setRoleReadonlyIn(readonlyIf.role_in || []);
        if (readonlyIf.value_equals) {
            const keys = Object.keys(readonlyIf.value_equals);
            if (keys.length > 0) {
                const k = keys[0];
                const v = readonlyIf.value_equals[k];
                setReadonlyCondField(k);
                setReadonlyCondValue(Array.isArray(v) ? v[0] : v);
            } else {
                setReadonlyCondField("");
                setReadonlyCondValue("");
            }
        } else {
            setReadonlyCondField("");
            setReadonlyCondValue("");
        }

        // Defaults
        setDefaultValue(ui.default != null ? ui.default : "");
        setDefaultExpression(
            ui.default_expression != null ? ui.default_expression : ""
        );
        setDefaultError("");
        setExpressionError(""); // reset dynamic error on open

        // Global options_source
        const optSrc = ui.options_source || null;
        if (!optSrc) {
            setOptionsSourceType("none");
            setStaticOptions([]);
        } else if (optSrc.type === "static") {
            setOptionsSourceType("static");
            setStaticOptions(optSrc.values || []);
        } else if (optSrc.type === "query") {
            setOptionsSourceType("query");
            setStaticOptions([]);
        }

        // Role-based options
        const ob = ui.options_by_role || {};
        const perRole = ob.per_role || {};
        setRoleOptionsByRole(perRole);
        const firstExistingRoleOpt = Object.keys(perRole)[0];
        const fallbackRoleOpt = roleValues[0] || null;
        setSelectedRoleForOptions(firstExistingRoleOpt || fallbackRoleOpt || null);
        setRbNewOptionLabel("");
        setRbNewOptionValue("");

        // Role-based hints
        const hintByRole = ui.hint_by_role || {};
        setRoleHints(hintByRole);
        const firstExistingHintRole = Object.keys(hintByRole)[0];
        const fallbackHintRole = roleValues[0] || null;
        const roleToUse = firstExistingHintRole || fallbackHintRole || null;
        setSelectedRoleForHint(roleToUse);
        setCurrentHintText(roleToUse ? hintByRole[roleToUse] || "" : "");

        // Badge settings
        const badgeSettings = ui.display_as_badge || {};
        setDisplayAsBadge(badgeSettings.enabled === true);
        setBadgeColor(badgeSettings.color || "#0d6efd");

        // Layout & widget
        setOrder(ui.order != null ? ui.order : "");
        const widget = ui.widget || {};
        setPlaceholder(widget.placeholder || "");
    }, [open, field, roleValues, required]); // include required so visibility resets when it changes [file:224]

    if (!open) return null;

    // --- small UI helpers ---

    /** Toggle a role in a given selected-list (for visible_if / readonly_if). */
    const handleToggleRoleList = (roleValue, list, setter) => {
        if (list.includes(roleValue)) {
            setter(list.filter((r) => r !== roleValue));
        } else {
            setter([...list, roleValue]);
        }
    };

    // --- Global static options handlers ---

    const handleAddStaticOption = () => {
        if (!newOptionLabel.trim() || !newOptionValue.trim()) return;
        setStaticOptions((prev) => [
            ...prev,
            { label: newOptionLabel.trim(), value: newOptionValue.trim() },
        ]);
        setNewOptionLabel("");
        setNewOptionValue("");
    };

    const handleRemoveStaticOption = (idx) => {
        setStaticOptions((prev) => prev.filter((_, i) => i !== idx));
    };

    // --- Role-based options handlers ---

    const usedRolesForOptions = Object.keys(roleOptionsByRole);
    const availableRolesForOptions = roleValues.filter(
        (r) => !usedRolesForOptions.includes(r)
    );
    const currentRoleOptionsList =
        selectedRoleForOptions && roleOptionsByRole[selectedRoleForOptions]
            ? roleOptionsByRole[selectedRoleForOptions]
            : [];

    const handleAddRoleOption = () => {
        if (!selectedRoleForOptions) return;
        if (!rbNewOptionLabel.trim() || !rbNewOptionValue.trim()) return;

        setRoleOptionsByRole((prev) => {
            const current = Array.isArray(prev[selectedRoleForOptions])
                ? prev[selectedRoleForOptions]
                : [];
            return {
                ...prev,
                [selectedRoleForOptions]: [
                    ...current,
                    {
                        label: rbNewOptionLabel.trim(),
                        value: rbNewOptionValue.trim(),
                    },
                ],
            };
        });
        setRbNewOptionLabel("");
        setRbNewOptionValue("");
    };

    const handleRemoveRoleOption = (idx) => {
        if (!selectedRoleForOptions) return;
        setRoleOptionsByRole((prev) => {
            const current = Array.isArray(prev[selectedRoleForOptions])
                ? prev[selectedRoleForOptions]
                : [];
            const next = current.filter((_, i) => i !== idx);
            return { ...prev, [selectedRoleForOptions]: next };
        });
    };

    // --- Role-based hints handlers ---

    const handleSelectHintRole = (role) => {
        setSelectedRoleForHint(role);
        setCurrentHintText(role ? roleHints[role] || "" : "");
    };

    const handleSaveHintForCurrentRole = () => {
        if (!selectedRoleForHint) return;
        const text = (currentHintText || "").trim();
        setRoleHints((prev) => ({
            ...prev,
            [selectedRoleForHint]: text,
        }));
    };

    const handleClearHintForCurrentRole = () => {
        if (!selectedRoleForHint) return;
        setRoleHints((prev) => {
            const next = { ...prev };
            delete next[selectedRoleForHint];
            return next;
        });
        setCurrentHintText("");
    };

    // --- Default input renderer based on data_type ---

    const renderDefaultInput = () => {
        const dt = field && field.data_type ? field.data_type.toLowerCase() : "";

        if (["integer", "float", "number", "decimal"].includes(dt)) {
            return (
                <input
                    type="number"
                    className="form-control"
                    value={defaultValue}
                    onChange={(e) => {
                        setDefaultValue(e.target.value);
                        if (defaultError) setDefaultError("");
                    }}
                    placeholder="numeric value"
                />
            );
        }

        if (dt === "boolean") {
            return (
                <select
                    className="form-control"
                    value={defaultValue}
                    onChange={(e) => {
                        setDefaultValue(e.target.value);
                        if (defaultError) setDefaultError("");
                    }}
                >
                    <option value="">None</option>
                    <option value="true">true</option>
                    <option value="false">false</option>
                </select>
            );
        }

        if (dt === "date" || dt === "datetime") {
            return (
                <input
                    type="date"
                    className="form-control"
                    value={defaultValue}
                    onChange={(e) => {
                        setDefaultValue(e.target.value);
                        if (defaultError) setDefaultError("");
                    }}
                    placeholder="YYYY-MM-DD"
                />
            );
        }

        const maxLen = getThisFieldStringMaxLength();
        return (
            <input
                className="form-control"
                value={defaultValue}
                onChange={(e) => {
                    setDefaultValue(e.target.value);
                    if (defaultError) setDefaultError("");
                }}
                placeholder="literal value"
                maxLength={maxLen}
            />
        );
    };

    // --- Condition value renderer for visible_if / readonly_if ---

    const renderCondValueInput = (selectedFieldName, value, setter) => {
        const dt = getFieldDataType(selectedFieldName);

        if (["integer", "float", "number", "decimal"].includes(dt)) {
            return (
                <input
                    type="number"
                    className="form-control"
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    placeholder="numeric value"
                />
            );
        }

        if (dt === "boolean") {
            return (
                <select
                    className="form-control"
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                >
                    <option value="">Any</option>
                    <option value="true">true</option>
                    <option value="false">false</option>
                </select>
            );
        }

        if (dt === "date" || dt === "datetime") {
            return (
                <input
                    type="date"
                    className="form-control"
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                />
            );
        }

        return (
            <input
                className="form-control"
                value={value}
                onChange={(e) => setter(e.target.value)}
                placeholder="value"
                maxLength={512}
            />
        );
    };

    // --- Save ui_rules with static + dynamic default validation ---

    const handleSave = () => {
        // Enforce static default if field is required
        if (required) {
            const trimmed = String(defaultValue ?? "").trim();
            if (!trimmed) {
                setDefaultError(
                    "Static default is required because this field is marked as required."
                );
                const modalBody = document.querySelector('.modal-body');
                if (modalBody) modalBody.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        }

        // Validate dynamic default expression
        const { valid, error } = validateDynamicExpression(defaultExpression);
        if (!valid) {
            setExpressionError(error);
            const modalBody = document.querySelector('.modal-body');
            if (modalBody) modalBody.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setExpressionError("");

        const ui = {};

        // Visible + visible_if
        ui.visible = true;
        const vIf = {};
        // When required, do not persist role-based visibility; otherwise keep existing behavior
        if (!required) {
            if (roleVisibleIn.length > 0) vIf.role_in = roleVisibleIn;
            if (visibleCondField && visibleCondValue) {
                vIf.value_equals = { [visibleCondField]: visibleCondValue };
            }
        }
        if (Object.keys(vIf).length > 0) ui.visible_if = vIf;

        // Read-only + readonly_if
        ui.readonly = readonly;
        const rIf = {};
        if (roleReadonlyIn.length > 0) rIf.role_in = roleReadonlyIn;
        if (readonlyCondField && readonlyCondValue) {
            rIf.value_equals = { [readonlyCondField]: readonlyCondValue };
        }
        if (Object.keys(rIf).length > 0) ui.readonly_if = rIf;

        // Defaults
        if (defaultValue !== "") ui.default = defaultValue;
        if (defaultExpression.trim() !== "") {
            ui.default_expression = defaultExpression.trim();
        }

        // Global options_source
        if (optionsSourceType === "static" && staticOptions.length > 0) {
            ui.options_source = {
                type: "static",
                values: staticOptions,
            };
        } else if (optionsSourceType === "query") {
            ui.options_source = {
                type: "query",
                source_name: "",
            };
        }

        // Role-based options (only when Source = static)
        if (optionsSourceType === "static") {
            const perRoleClean = Object.fromEntries(
                Object.entries(roleOptionsByRole).filter(
                    ([, list]) => Array.isArray(list) && list.length > 0
                )
            );
            if (Object.keys(perRoleClean).length > 0) {
                ui.options_by_role = { per_role: perRoleClean };
            }
        }

        // Role-based hints
        const cleanHints = Object.fromEntries(
            Object.entries(roleHints).filter(
                ([, text]) => typeof text === "string" && text.trim().length > 0
            )
        );
        if (Object.keys(cleanHints).length > 0) {
            ui.hint_by_role = cleanHints;
        }

        // Badge settings
        ui.display_as_badge = { enabled: displayAsBadge, color: badgeColor };

        // Order
        if (order !== "" && !isNaN(Number(order))) {
            ui.order = Number(order);
        }

        // Widget
        const widget = {};
        if (placeholder) widget.placeholder = placeholder;
        if (Object.keys(widget).length > 0) ui.widget = widget;

        onSave(ui);
        onClose();
    };

    // Other fields for conditions (exclude current field)
    const otherFields = (fields || []).filter(
        (f) => !field || f.name !== field.name
    );

    return (
        <div
            className="modal fade show"
            style={{ display: "block", background: "rgba(0,0,0,0.40)" }}
            role="dialog"
        >
            <div className="modal-dialog modal-lg" role="document">
                <div
                    className="modal-content"
                    style={{ borderRadius: theme.cardRadius, border: "none" }}
                >
                    {/* Header */}
                    <div
                        className="modal-header"
                        style={{
                            borderBottom: `1px solid ${theme.primary}33`,
                            background: "#fafbff",
                        }}
                    >
                        <h5 className="modal-title" style={{ color: theme.primary }}>
                            Configure UI Rules for{" "}
                            <code>{field && (field.display_name || field.name)}</code>
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    {/* Body */}
                    <div
                        className="modal-body"
                        style={{ maxHeight: "70vh", overflowY: "auto", paddingTop: 16 }}
                    >
                        {/* VISIBILITY SECTION */}
                        <div
                            style={{
                                borderBottom: `1px dashed ${theme.primary}33`,
                                paddingBottom: 12,
                                marginBottom: 16,
                            }}
                        >
                            <h6
                                style={{
                                    color: theme.primary,
                                    fontWeight: 600,
                                    marginBottom: 8,
                                }}
                            >
                                Visibility
                            </h6>

                            <div className="mb-2">
                                <label className="form-check-label me-3">
                                    <input
                                        type="checkbox"
                                        className="form-check-input me-1"
                                        checked={visible}
                                        disabled={required}
                                        onChange={(e) => setVisible(e.target.checked)}
                                    />
                                    Always visible
                                    {required && (
                                        <span
                                            style={{
                                                fontSize: 12,
                                                color: "#999",
                                                marginLeft: 6,
                                            }}
                                        >
                                            (required fields must always be visible)
                                        </span>
                                    )}
                                </label>
                            </div>

                            {/* Hide role-based visibility when required */}
                            {!required && (
                                <div className="mb-3">
                                    <div
                                        style={{
                                            fontSize: 13,
                                            color: "#636979",
                                            marginBottom: 4,
                                        }}
                                    >
                                        Visible only for selected roles:
                                    </div>
                                    {roleValues.length === 0 ? (
                                        <div style={{ fontSize: 12, color: "#999" }}>
                                            No roles configured. You can still use column-based
                                            conditions below.
                                        </div>
                                    ) : (
                                        <div
                                            className="d-flex flex-wrap"
                                            style={{ gap: 8, marginTop: 4 }}
                                        >
                                            {roleValues.map((roleValue) => {
                                                const isActive =
                                                    roleVisibleIn.indexOf(roleValue) !== -1;
                                                return (
                                                    <button
                                                        key={roleValue}
                                                        type="button"
                                                        onClick={() =>
                                                            handleToggleRoleList(
                                                                roleValue,
                                                                roleVisibleIn,
                                                                setRoleVisibleIn
                                                            )
                                                        }
                                                        className="btn btn-sm"
                                                        style={{
                                                            borderRadius: 20,
                                                            border: `1px solid ${theme.primary}`,
                                                            background: isActive ? theme.primary : "white",
                                                            color: isActive ? "white" : theme.primary,
                                                        }}
                                                    >
                                                        {getRoleLabel(roleValue)}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Column-based visible_if currently hidden via d-none but logic kept */}
                            {!required && (
                                <div className="mb-0 d-none">
                                    <label className="form-label">
                                        Visible when column value equals
                                    </label>
                                    <div className="d-flex gap-2">
                                        <select
                                            className="form-control"
                                            value={visibleCondField}
                                            onChange={(e) => {
                                                setVisibleCondField(e.target.value);
                                                setVisibleCondValue("");
                                            }}
                                        >
                                            <option value="">Select column</option>
                                            {otherFields.map((f) => (
                                                <option key={f.name} value={f.name}>
                                                    {f.display_name || f.name}
                                                </option>
                                            ))}
                                        </select>

                                        {renderCondValueInput(
                                            visibleCondField,
                                            visibleCondValue,
                                            setVisibleCondValue
                                        )}
                                    </div>
                                    <div
                                        style={{ fontSize: 12, color: "#999", marginTop: 2 }}
                                    >
                                        The comparison value uses the same data type as the selected
                                        column.
                                    </div>
                                </div>
                            )}
                        </div>


                        {/* READONLY SECTION */}
                        <div
                            style={{
                                borderBottom: `1px dashed ${theme.primary}33`,
                                paddingBottom: 12,
                                marginBottom: 16,
                            }}
                        >
                            <h6
                                style={{
                                    color: theme.primary,
                                    fontWeight: 600,
                                    marginBottom: 8,
                                }}
                            >
                                Read-only
                            </h6>

                            <div className="mb-2">
                                <label className="form-check-label me-3">
                                    <input
                                        type="checkbox"
                                        className="form-check-input me-1"
                                        checked={readonly}
                                        onChange={(e) => setReadonly(e.target.checked)}
                                    />
                                    Always read-only
                                </label>
                            </div>

                            <div className="mb-3">
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: "#636979",
                                        marginBottom: 4,
                                    }}
                                >
                                    Read-only for these roles:
                                </div>
                                {roleValues.length === 0 ? (
                                    <div style={{ fontSize: 12, color: "#999" }}>
                                        No roles configured. You can still use column-based
                                        conditions below.
                                    </div>
                                ) : (
                                    <div
                                        className="d-flex flex-wrap"
                                        style={{ gap: 8, marginTop: 4 }}
                                    >
                                        {roleValues.map((roleValue) => {
                                            const isActive =
                                                roleReadonlyIn.indexOf(roleValue) !== -1;
                                            return (
                                                <button
                                                    key={roleValue}
                                                    type="button"
                                                    onClick={() =>
                                                        handleToggleRoleList(
                                                            roleValue,
                                                            roleReadonlyIn,
                                                            setRoleReadonlyIn
                                                        )
                                                    }
                                                    className="btn btn-sm"
                                                    style={{
                                                        borderRadius: 20,
                                                        border: `1px solid ${theme.primary}`,
                                                        background: isActive ? theme.primary : "white",
                                                        color: isActive ? "white" : theme.primary,
                                                    }}
                                                >
                                                    {getRoleLabel(roleValue)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="mb-0 d-none">
                                <label className="form-label">
                                    Read-only when column value equals
                                </label>
                                <div className="d-flex gap-2">
                                    <select
                                        className="form-control"
                                        value={readonlyCondField}
                                        onChange={(e) => {
                                            setReadonlyCondField(e.target.value);
                                            setReadonlyCondValue("");
                                        }}
                                    >
                                        <option value="">Select column</option>
                                        {otherFields.map((f) => (
                                            <option key={f.name} value={f.name}>
                                                {f.display_name || f.name}
                                            </option>
                                        ))}
                                    </select>

                                    {renderCondValueInput(
                                        readonlyCondField,
                                        readonlyCondValue,
                                        setReadonlyCondValue
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* DEFAULTS SECTION */}
                        <div
                            style={{
                                borderBottom: `1px dashed ${theme.primary}33`,
                                paddingBottom: 12,
                                marginBottom: 16,
                            }}
                        >
                            <h6
                                style={{
                                    color: theme.primary,
                                    fontWeight: 600,
                                    marginBottom: 8,
                                }}
                            >
                                Default Value
                            </h6>

                            <div className="mb-3">
                                <label className="form-label">
                                    Static default
                                    {required && (
                                        <span style={{ color: "red" }}>
                                            {" "}
                                            * (required for this field)
                                        </span>
                                    )}
                                </label>
                                {renderDefaultInput()}
                                {defaultError && (
                                    <div
                                        style={{
                                            color: "#c80b23",
                                            fontSize: 13,
                                            marginTop: 4,
                                        }}
                                    >
                                        {defaultError}
                                    </div>
                                )}
                                <div
                                    style={{ fontSize: 12, color: "#999", marginTop: 2 }}
                                >
                                    Static default applied when creating a new record. The input
                                    respects this field’s data type, and text values are limited
                                    by the column’s maximum length.
                                </div>
                            </div>

                            <div className="mb-0 d-none">
                                <label className="form-label">
                                    Dynamic default (expression)
                                </label>
                                <input
                                    className="form-control"
                                    value={defaultExpression}
                                    onChange={(e) => {
                                        setDefaultExpression(e.target.value);
                                        if (expressionError) setExpressionError("");
                                    }}
                                    placeholder="e.g. current_user.email or form.quantity * form.price"
                                />
                                {expressionError && (
                                    <div
                                        style={{
                                            color: "#c80b23",
                                            fontSize: 13,
                                            marginTop: 4,
                                        }}
                                    >
                                        {expressionError}
                                    </div>
                                )}
                                <div
                                    style={{ fontSize: 12, color: "#999", marginTop: 2 }}
                                >
                                    Evaluated with context: <code>form</code>,{" "}
                                    <code>current_user</code>, <code>now()</code>. Dynamic
                                    default takes precedence over the static default when both are
                                    provided.
                                </div>

                                {/* Expandable help about valid expressions */}
                                <div style={{ marginTop: 6 }}>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0"
                                        style={{
                                            fontSize: 12,
                                            textDecoration: "none",
                                            color: theme.primary,
                                        }}
                                        onClick={() =>
                                            setShowDynamicInfo((prev) => !prev)
                                        }
                                    >
                                        <i
                                            className={
                                                showDynamicInfo
                                                    ? "bi bi-chevron-down me-1"
                                                    : "bi bi-chevron-right me-1"
                                            }
                                        />
                                        {showDynamicInfo
                                            ? "Hide dynamic default examples"
                                            : "Show valid dynamic default patterns"}
                                    </button>

                                    {showDynamicInfo && (
                                        <div
                                            style={{
                                                marginTop: 6,
                                                padding: 8,
                                                borderRadius: 6,
                                                background: "#f7f8ff",
                                                border: `1px solid ${theme.primary}22`,
                                                fontSize: 12,
                                                color: "#555",
                                            }}
                                        >
                                            <div style={{ marginBottom: 4 }}>
                                                Valid expressions:
                                            </div>
                                            <ul style={{ paddingLeft: 18, marginBottom: 4 }}>
                                                <li>Single value: <code>now()</code></li>
                                                <li>
                                                    User context:{" "}
                                                    <code>current_user.email</code>,{" "}
                                                    <code>current_user.username</code>
                                                </li>
                                                <li>
                                                    Field value: <code>form.quantity</code>,{" "}
                                                    <code>form.price</code>, etc.
                                                </li>
                                                <li>
                                                    Binary expression between two values of the same
                                                    kind, e.g.{" "}
                                                    <code>form.quantity * form.price</code> or{" "}
                                                    <code>form.discount &gt;= form.min_discount</code>.
                                                </li>
                                                <li>
                                                    Text concatenation using <code>+</code> with single
                                                    quotes, e.g.{" "}
                                                    <code>'MR ' + form.name + ' SUF'</code> or{" "}
                                                    <code>'PRE ' + current_user.email</code>.
                                                </li>
                                            </ul>
                                            <div>
                                                Not allowed:
                                                <ul style={{ paddingLeft: 18, marginTop: 2 }}>
                                                    <li>
                                                        Mixing categories in one binary expression, e.g.{" "}
                                                        <code>now() + form.price</code>.
                                                    </li>
                                                    <li>
                                                        Using undefined identifiers or unquoted text, e.g.{" "}
                                                        <code>foo</code> or <code>MR + form.name</code>.
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* HINTS SECTION */}
                        <div
                            style={{
                                borderBottom: `1px dashed ${theme.primary}33`,
                                paddingBottom: 12,
                                marginBottom: 16,
                            }}
                        >
                            <h6
                                style={{
                                    color: theme.primary,
                                    fontWeight: 600,
                                    marginBottom: 8,
                                }}
                            >
                                Hints by role
                            </h6>

                            <div
                                style={{
                                    fontSize: 12,
                                    color: "#999",
                                    marginBottom: 6,
                                }}
                            >
                                A hint is short helper text shown near the field to guide users
                                on what to enter or how to use this field. Different roles can
                                see different hints. Each hint can be up to 512 characters.
                            </div>

                            {roleValues.length === 0 ? (
                                <div
                                    style={{ fontSize: 12, color: "#999", marginBottom: 8 }}
                                >
                                    No roles configured. Role-based hints require at least one
                                    role.
                                </div>
                            ) : (
                                <>
                                    <div
                                        className="mb-2 d-flex flex-wrap"
                                        style={{ gap: 6 }}
                                    >
                                        {roleValues.map((role) => {
                                            const hasHint =
                                                roleHints[role] &&
                                                roleHints[role].trim().length > 0;
                                            const isActive = selectedRoleForHint === role;
                                            return (
                                                <Button
                                                    key={role}
                                                    type="button"
                                                    mode={
                                                        isActive
                                                            ? "primary-bold"
                                                            : hasHint
                                                                ? "bordered"
                                                                : "outlined"
                                                    }
                                                    small
                                                    onClick={() => handleSelectHintRole(role)}
                                                    style={{ borderRadius: 16 }}
                                                >
                                                    {getRoleLabel(role)}
                                                </Button>
                                            );
                                        })}
                                    </div>

                                    {selectedRoleForHint && (
                                        <div className="mb-0">
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: "#636979",
                                                    marginBottom: 4,
                                                }}
                                            >
                                                Hint for{" "}
                                                <b>{getRoleLabel(selectedRoleForHint)}</b>
                                            </div>
                                            <textarea
                                                className="form-control"
                                                rows={2}
                                                value={currentHintText}
                                                onChange={(e) =>
                                                    setCurrentHintText(
                                                        e.target.value.slice(0, 512)
                                                    )
                                                }
                                                placeholder="Short, role-specific guidance shown near this field. Max 512 characters."
                                                maxLength={512}
                                            />
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "flex-end",
                                                    gap: 8,
                                                    marginTop: 6,
                                                }}
                                            >
                                                <Button
                                                    type="button"
                                                    mode="outlined"
                                                    small
                                                    onClick={handleClearHintForCurrentRole}
                                                >
                                                    Clear
                                                </Button>
                                                <Button
                                                    type="button"
                                                    mode="primary-bold"
                                                    small
                                                    onClick={handleSaveHintForCurrentRole}
                                                >
                                                    Save hint
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* OPTIONS SECTION (enum/string/text) */}
                        {field &&
                            (field.data_type === "enum" ||
                                field.data_type === "string" ||
                                field.data_type === "text") && (
                                <div
                                    style={{
                                        borderBottom: `1px dashed ${theme.primary}33`,
                                        paddingBottom: 12,
                                        marginBottom: 16,
                                    }}
                                >
                                    <h6
                                        style={{
                                            color: theme.primary,
                                            fontWeight: 600,
                                            marginBottom: 8,
                                        }}
                                    >
                                        Options
                                    </h6>

                                    {/* Source selector */}
                                    <div className="mb-2">
                                        <label className="form-label me-2">Source</label>
                                        <select
                                            className="form-control"
                                            style={{ maxWidth: 220 }}
                                            value={optionsSourceType}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setOptionsSourceType(val);
                                            }}
                                        >
                                            <option value="none">None</option>
                                            <option value="static">Static list</option>
                                            {/* reserved for future:
                       * <option value="query">Query</option>
                       */}
                                        </select>
                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: "#999",
                                                marginTop: 2,
                                            }}
                                        >
                                            <b>None</b> keeps this field as free text.{" "}
                                            <b>Static list</b> defines a fixed set of choices,
                                            and you can optionally override them per role.
                                        </div>
                                    </div>

                                    {/* Only show list editors when Source = Static list */}
                                    {optionsSourceType === "static" && (
                                        <>
                                            {/* Global static list (shared by default) */}
                                            <div className="mb-4">
                                                <label className="form-label">
                                                    Global static list
                                                </label>
                                                <div className="d-flex gap-2 mb-2">
                                                    <input
                                                        className="form-control"
                                                        placeholder="Label"
                                                        value={newOptionLabel}
                                                        maxLength={512}
                                                        onChange={(e) =>
                                                            setNewOptionLabel(e.target.value)
                                                        }
                                                    />
                                                    <input
                                                        className="form-control"
                                                        placeholder="Value"
                                                        value={newOptionValue}
                                                        maxLength={512}
                                                        onChange={(e) =>
                                                            setNewOptionValue(e.target.value)
                                                        }
                                                    />
                                                    <Button
                                                        type="button"
                                                        mode="outlined"
                                                        small
                                                        onClick={handleAddStaticOption}
                                                    >
                                                        Add
                                                    </Button>
                                                </div>

                                                {staticOptions.length > 0 && (
                                                    <ul className="list-group">
                                                        {staticOptions.map((opt, i) => (
                                                            <li
                                                                key={opt.value + "-" + i}
                                                                className="list-group-item d-flex justify-content-between align-items-center"
                                                            >
                                                                <span>
                                                                    {opt.label}{" "}
                                                                    <code>({opt.value})</code>
                                                                </span>
                                                                <Button
                                                                    type="button"
                                                                    mode="outlined"
                                                                    small
                                                                    onClick={() =>
                                                                        handleRemoveStaticOption(i)
                                                                    }
                                                                >
                                                                    ✕
                                                                </Button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        color: "#999",
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    This list is used for all users unless a
                                                    role-specific list is defined below for
                                                    their role.
                                                </div>
                                            </div>

                                            {/* Role-based options */}
                                            <div className="mb-0">
                                                <label className="form-label">
                                                    Role-specific lists
                                                </label>
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        color: "#999",
                                                        marginBottom: 6,
                                                    }}
                                                >
                                                    Add a role once and configure its own
                                                    static list. When a user has that role, its
                                                    list overrides the global static list.
                                                </div>

                                                {/* Chips for roles that already have lists */}
                                                <div
                                                    className="mb-2 d-flex flex-wrap"
                                                    style={{ gap: 6 }}
                                                >
                                                    {Object.keys(roleOptionsByRole).map((role) => (
                                                        <Button
                                                            key={role}
                                                            type="button"
                                                            mode={
                                                                selectedRoleForOptions === role
                                                                    ? "primary-bold"
                                                                    : "outlined"
                                                            }
                                                            small
                                                            onClick={() =>
                                                                setSelectedRoleForOptions(role)
                                                            }
                                                            style={{ borderRadius: 16 }}
                                                        >
                                                            {getRoleLabel(role)}
                                                        </Button>
                                                    ))}
                                                </div>

                                                {/* Add role dropdown (only for roles not yet used) */}
                                                {availableRolesForOptions.length > 0 && (
                                                    <div className="d-flex gap-2 mb-2 align-items-center">
                                                        <select
                                                            className="form-control"
                                                            style={{ maxWidth: 220 }}
                                                            value=""
                                                            onChange={(e) => {
                                                                const role = e.target.value;
                                                                if (!role) return;
                                                                setRoleOptionsByRole((prev) => ({
                                                                    ...prev,
                                                                    [role]: prev[role] || [],
                                                                }));
                                                                setSelectedRoleForOptions(role);
                                                            }}
                                                        >
                                                            <option value="">
                                                                Add role…
                                                            </option>
                                                            {availableRolesForOptions.map((rv) => (
                                                                <option key={rv} value={rv}>
                                                                    {getRoleLabel(rv)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <span
                                                            style={{
                                                                fontSize: 12,
                                                                color: "#636979",
                                                            }}
                                                        >
                                                            Each role can be added only once.
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Editor for currently selected role list */}
                                                {selectedRoleForOptions && (
                                                    <>
                                                        <div
                                                            style={{
                                                                fontSize: 12,
                                                                color: "#636979",
                                                                marginBottom: 4,
                                                            }}
                                                        >
                                                            Editing list for{" "}
                                                            <b>
                                                                {getRoleLabel(
                                                                    selectedRoleForOptions
                                                                )}
                                                            </b>
                                                        </div>
                                                        <div className="d-flex gap-2 mb-2">
                                                            <input
                                                                className="form-control"
                                                                placeholder="Label"
                                                                value={rbNewOptionLabel}
                                                                maxLength={512}
                                                                onChange={(e) =>
                                                                    setRbNewOptionLabel(
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                            <input
                                                                className="form-control"
                                                                placeholder="Value"
                                                                value={rbNewOptionValue}
                                                                maxLength={512}
                                                                onChange={(e) =>
                                                                    setRbNewOptionValue(
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                            <Button
                                                                type="button"
                                                                mode="outlined"
                                                                small
                                                                onClick={handleAddRoleOption}
                                                            >
                                                                Add
                                                            </Button>
                                                        </div>

                                                        {currentRoleOptionsList.length > 0 && (
                                                            <ul className="list-group">
                                                                {currentRoleOptionsList.map(
                                                                    (opt, i) => (
                                                                        <li
                                                                            key={
                                                                                selectedRoleForOptions +
                                                                                "-" +
                                                                                opt.value +
                                                                                "-" +
                                                                                i
                                                                            }
                                                                            className="list-group-item d-flex justify-content-between align-items-center"
                                                                        >
                                                                            <span>
                                                                                {opt.label}{" "}
                                                                                <code>
                                                                                    ({opt.value})
                                                                                </code>
                                                                            </span>
                                                                            <Button
                                                                                type="button"
                                                                                mode="outlined"
                                                                                small
                                                                                onClick={() =>
                                                                                    handleRemoveRoleOption(
                                                                                        i
                                                                                    )
                                                                                }
                                                                            >
                                                                                ✕
                                                                            </Button>
                                                                        </li>
                                                                    )
                                                                )}
                                                            </ul>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                        {/* LAYOUT & WIDGET SECTION */}
                        <div>
                            <h6
                                style={{
                                    color: theme.primary,
                                    fontWeight: 600,
                                    marginBottom: 8,
                                }}
                            >
                                Layout & Widget
                            </h6>

                            <div className="mb-3 d-none">
                                <label className="form-label">Order</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="e.g. 10"
                                    value={order}
                                    onChange={(e) =>
                                        setOrder(
                                            e.target.value === "" ? "" : Number(e.target.value)
                                        )
                                    }
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Placeholder</label>
                                <input
                                    className="form-control"
                                    value={placeholder}
                                    onChange={(e) => setPlaceholder(e.target.value)}
                                    maxLength={512}
                                />
                                <div
                                    style={{ fontSize: 12, color: "#999", marginTop: 2 }}
                                >
                                    Placeholder text shown inside this field on Insert and
                                    Update forms until the user enters a value.
                                </div>
                            </div>

                            <div className="mb-0">
                                <div className="mb-2">
                                    <ThemedToggle
                                        checked={displayAsBadge}
                                        onChange={setDisplayAsBadge}
                                        onLabel="Display as Badge"
                                        offLabel="Display as Badge"
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <label className="form-label mb-0" style={{ fontSize: 13, color: displayAsBadge ? '#636979' : '#a0a5b1' }}>Badge Color:</label>
                                    <input
                                        type="color"
                                        className="form-control form-control-color"
                                        style={{ width: 40, padding: 2, height: 32 }}
                                        value={badgeColor}
                                        onChange={(e) => setBadgeColor(e.target.value)}
                                        disabled={!displayAsBadge}
                                        title="Choose your badge color"
                                    />
                                </div>
                                <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                                    If enabled, values for this column will be rendered as colored badges in the application table views.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div
                        className="modal-footer"
                        style={{ borderTop: `1px solid ${theme.primary}33` }}
                    >
                        <Button mode="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button mode="primary-bold" onClick={handleSave}>
                            Save Rules
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
